import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { UserInvite } from '../../types/settings';
import { Mail, UserPlus, RefreshCw } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import toast from 'react-hot-toast';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function AdminPage() {
  useDocumentTitle('Admin');
  
  const [invites, setInvites] = useState<UserInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newInvite, setNewInvite] = useState({
    email: '',
    role: 'staff' as const
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'invites'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const inviteData: UserInvite[] = [];
        snapshot.forEach((doc) => {
          inviteData.push({ id: doc.id, ...doc.data() } as UserInvite);
        });
        setInvites(inviteData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading invites:', error);
        toast.error('Failed to load invites');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

      await addDoc(collection(db, 'invites'), {
        ...newInvite,
        status: 'pending',
        createdAt: serverTimestamp(),
        expiresAt: expiresAt.toISOString(),
        schoolId: 'your-school-id', // You would get this from context/state
        invitedBy: 'current-user-id' // You would get this from auth context
      });

      toast.success('Invitation sent successfully');
      setShowInviteModal(false);
      setNewInvite({ email: '', role: 'staff' });
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error('Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-text-primary">User Management</h1>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                       transition-colors flex items-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              Invite Staff
            </button>
          </div>

          {/* Pending Invites */}
          <div className="bg-surface-light rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Pending Invitations</h2>
            
            <div className="space-y-4">
              {invites.filter(invite => invite.status === 'pending').map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-4 bg-background rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-primary-50 rounded-lg">
                      <Mail className="w-5 h-5 text-primary-500" />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{invite.email}</p>
                      <p className="text-sm text-text-secondary">
                        Invited {new Date(invite.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-warning-DEFAULT">Pending</span>
                    <button
                      onClick={() => {/* Implement resend invite */}}
                      className="p-2 text-text-secondary hover:text-primary-500 transition-colors"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}

              {invites.filter(invite => invite.status === 'pending').length === 0 && (
                <p className="text-text-secondary text-center py-4">No pending invitations</p>
              )}
            </div>
          </div>

          {/* Invite Modal */}
          {showInviteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-md mx-4 p-6">
                <h2 className="text-xl font-semibold text-text-primary mb-4">Invite Staff Member</h2>
                
                <form onSubmit={handleInvite} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={newInvite.email}
                      onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Role
                    </label>
                    <select
                      value={newInvite.role}
                      onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value as 'admin' | 'staff' })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    >
                      <option value="staff">Staff Member</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowInviteModal(false)}
                      className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                               transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Send Invitation
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}